import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitle, IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ConfirmationDialogResult, RecordUserAttachmentCategory } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemTransferredProperty, RecordEditItemTransferredProperty, RecordEditItemTransferredPropertySave } from "../../../models/record/recordEditItemTransferredProperty";
import * as recordService from "../../../models/services/recordService";
import * as storeActions from "../../../models/store/storeActions";
import { StoreUtils } from "../../../models/store/storeUtils";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { Autocomplete } from "../../framework/Form/Autocomplete";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { RecordEditAttachmentList } from "../RecordEdit/RecordEditAttachmentList";

export interface IRecordUserTransferredPropertyDialogProp {
    classes?: string;
    companyId: string;
    recordId: string;
    userCode: string;
    transferredPropertyTypes: string[];
    item: IRecordEditItemTransferredProperty;
    attachments: IFileAttachment[];
    hasPrevious: boolean;
    hasNext: boolean;
    onCustomSave?: (saveItem: RecordEditItemTransferredPropertySave) => Promise<ApiSuccess>;
    onSaved: (item: IRecordEditItemTransferredProperty, closeDialog: boolean) => void;
    onClose: () => void;
    onPrevious: () => void;
    onNext: () => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

export interface IRecordUserTransferredPropertyDialogState {
    type: string;
    code: string;
    description: string;
    transferDate: string;
    returnDate: string;
    transferredPropertyTypes: IIdTitle[];
    attachments: IFileAttachment[];
}

export class RecordUserTransferredPropertyDialog extends React.Component<IRecordUserTransferredPropertyDialogProp, IRecordUserTransferredPropertyDialogState> {
    private orgStateHash: string = "";
    
    getStateFromProps = (props: IRecordUserTransferredPropertyDialogProp): IRecordUserTransferredPropertyDialogState => {
        const item = props.item;
        const transferredPropertyTypes = props.transferredPropertyTypes.slice(0);
        transferredPropertyTypes.sort(Base.strCompare);
        return {
            type: item.type,
            code: item.code,
            description: item.description,
            transferDate: Base.utcTimeToDateStr(item.transferDate),
            returnDate: Base.utcTimeToDateStr(item.returnDate),
            transferredPropertyTypes: transferredPropertyTypes.map(i => IdTitle.createIdTitleSimple(i)),
            attachments: props.attachments.filter(i => i.subOwnerId === item.id)
        };
    };

    constructor(props: IRecordUserTransferredPropertyDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    async componentDidUpdate(prevProps: IRecordUserTransferredPropertyDialogProp, prevState: IRecordUserTransferredPropertyDialogState) {
        const props = this.props;
        if (prevProps.item.getHash() === props.item.getHash() && EditItem.getHash(prevProps.attachments) === EditItem.getHash(props.attachments)) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getHashFromState(props, state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "transferredPropertyCode") {
            this.setState({ code: value });
        } else if (name === "description") {
            this.setState({ description: value });
        }
    };

    handleTypeAutoCompleteChange = (value: string) => {
        this.setState({ type: value });
    };

    dateChange = (transferDate: Date, returnDate: Date) => {
        if (!Base.isNullOrUndefined(transferDate) && !Base.isNullOrUndefined(returnDate) && returnDate < transferDate) {
            returnDate = transferDate;
        }
        const transferDateStr = Base.utcTimeToDateStr(transferDate.getTime());
        const returnDateStr = returnDate ? Base.utcTimeToDateStr(returnDate.getTime()) : "";
        this.setState({
            transferDate: transferDateStr,
            returnDate: returnDateStr
        });
    };

    handleTransferDateChange = (value: string) => {
        this.setState({ transferDate: value });
    };

    handleTransferDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(value ? value.toUtcDate() : Base.getNowUtcDate().addDays(-1), state.returnDate ? state.returnDate.toUtcDate() : null);
    };

    handleReturnDateChange = (value: string) => {
        this.setState({ returnDate: value });
    };

    handleReturnDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(state.transferDate ? state.transferDate.toUtcDate() : Base.getNowUtcDate(), value ? value.toUtcDate() : null);
    };

    getHashFromState = (props: IRecordUserTransferredPropertyDialogProp, state: IRecordUserTransferredPropertyDialogState): string => {
        const result = new SaveDataObj();
        result.append("type", state.type);
        result.append("code", state.code);
        result.append("description", state.description);
        result.append("transferDate", state.transferDate);
        result.append("returnDate", !state.returnDate ? null : state.returnDate);
        return result.hash;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.type) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TypeMustBeDefined));
                return resolve(false);
            }
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TitleMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };
    
    getSaveData = (props: IRecordUserTransferredPropertyDialogProp, item: RecordEditItemTransferredPropertySave): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("transferredProperty", item);
        return result;
    };

    saveToDb = async (): Promise<RecordEditItemTransferredProperty> => {
        const state = this.state;
        const props = this.props;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        // Set data
        const item = new RecordEditItemTransferredProperty(this.props.item);
        item.type = state.type;
        item.code = state.code;
        item.description = state.description;
        item.transferDate = state.transferDate.toUtcDate().getTime();
        item.returnDate = state.returnDate ? state.returnDate.toUtcDate().getTime() : null;
        if (props.onCustomSave) {
            const customResult = await props.onCustomSave(new RecordEditItemTransferredPropertySave(item));
            if (!customResult) return null;
            return item;
        }
        const saveData = this.getSaveData(this.props, new RecordEditItemTransferredPropertySave(item));
        // Call server
        const result = await AppUtils.callService(() => recordService.recordEditSaveTransferredProperty(saveData.obj));
        if (!result) return null;
        this.orgStateHash = this.getHashFromState(this.props, this.state);
        item.id = result.id;
        item.rowId = result.rowId;
        item.modifyUser = StoreUtils.getNowModifyUser();
        return item;
    };

    saveDialog = async (close: boolean) => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onSaved(result, close);
        }
        return result;
    };

    handleSave = async (closeDialog: boolean): Promise<RecordEditItemTransferredProperty> => {
        return await this.saveDialog(closeDialog);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveDialog(false),
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        return (
            <Dialog
                id="record-user-transferred-property-dialog"
                maxWidth="sm"
                title={isNew ? Translations.AddTransferredProperty : Translations.EditTransferredProperty + ": " + props.item.getUiTitle()}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.AType} required margin={0}>
                                    <Autocomplete
                                        value={state.type}
                                        maxLength={50}
                                        options={state.transferredPropertyTypes}
                                        onChange={this.handleTypeAutoCompleteChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Title} required>
                                    <TextField type="text" name="transferredPropertyCode" title={Translations.Title} inputProps={{ maxLength: 100 }} value={state.code} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.PropertyTransferred} required>
                                    <DatePicker
                                        value={state.transferDate}
                                        required={true}
                                        onChange={this.handleTransferDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.Returned + " / " + Translations.Removed}>
                                    <DatePicker
                                        value={state.returnDate}
                                        onChange={this.handleReturnDateChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.AdditionalInformation}>
                                    <TextField name="description" title={Translations.AdditionalInformation} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.description} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {!!props.onAttachmentsAdded &&
                            <GridRow>
                                <Grid item xs>
                                    <RecordEditAttachmentList
                                        isDisabled={isNew}
                                        isDisabledMessage={Translations.NewTransferredPropertyMustBeSavedBeforeAttachmentsCanBeAdded}
                                        companyId={props.companyId}
                                        recordId={props.recordId}
                                        subOwnerId={props.item.id}
                                        userCode={props.userCode}
                                        category={RecordUserAttachmentCategory.TransferredProperty}
                                        attachments={state.attachments}
                                        onRemoved={props.onAttachmentsRemoved}
                                        onAdded={props.onAttachmentsAdded}
                                    />
                                </Grid>
                            </GridRow>
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? props.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={!isNew ? this.handlePrevious : null}
                            onNext={!isNew ? this.handleNext : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}